import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { Input } from 'components/Form/Input/Input';
import { PhoneInput } from 'components/Form/PhoneInput/PhoneInput';
import { SelectAutocomplete } from 'components/Form/SelectAutocomplete/SelectAutocomplete';
import { Button } from 'components/UI/Button/Button';
import { useDebouncedEffect } from 'hooks/useDebouncedEffect';
import { useMarketplace } from 'hooks/useMarketplace';
import { useMasterData } from 'hooks/useMasterData';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import ReactInputMask from 'react-input-mask';
import { Customer } from 'types/Customer';
import { IntakeFormCustomQuestion } from 'types/IntakeForm';
import { MarketplaceType } from 'types/Marketplace';

import classes from './CustomerForm.module.scss';

type CustomerFormState = {
    [K in keyof Customer]: Customer[K] | null;
};

interface CustomerFormProps {
    defaultValues?: Customer;
    questions?: Array<IntakeFormCustomQuestion>;
    onChange: (isValid: boolean) => void;
    onSubmit: (customer: Customer) => void;
}

export const CustomerForm = React.forwardRef<{ submit: () => void }, CustomerFormProps>(
    (props, ref) => {
        const { businessType, address: marketplaceAddress } = useMarketplace();
        const {
            masterData: { relationships }
        } = useMasterData();
        const formRef = React.useRef<HTMLFormElement>(null);

        const [showEmergencyContact, setShowEmergencyContact] = React.useState(
            (props.defaultValues?.emergencyContacts &&
                props.defaultValues?.emergencyContacts.length > 0) ||
                false
        );
        const [prevShowEmergencyContact, setPrevShowEmergencyContact] = React.useState(false);

        const {
            trigger,
            control,
            register,
            setValue,
            handleSubmit,
            formState: { isValid, errors }
        } = useForm<CustomerFormState>({
            mode: 'onChange',
            defaultValues: props.defaultValues
        });

        const isAddressRequired = React.useMemo(() => {
            return (
                businessType === MarketplaceType.INHOME || businessType === MarketplaceType.MOBILE
            );
        }, [businessType]);

        React.useEffect(() => {
            if (prevShowEmergencyContact !== showEmergencyContact) {
                // Only trigger validation when value changes
                trigger();
                setPrevShowEmergencyContact(showEmergencyContact);
            }
        }, [prevShowEmergencyContact, showEmergencyContact, trigger]);

        useDebouncedEffect(
            () => {
                props.onChange(isValid);
            },
            100,
            [isValid, props]
        );

        React.useImperativeHandle(ref, () => ({
            submit: () => {
                return handleSubmit(submitHandler)();
            }
        }));

        const deleteEmergencyContactHandler = () => {
            setShowEmergencyContact(false);
            setValue('emergencyContacts', []);
        };

        const submitHandler = async (data: CustomerFormState) => {
            props.onSubmit(data as Customer);
        };

        const questionChangeHandler = (
            index: number,
            question: IntakeFormCustomQuestion,
            answer: string
        ) => {
            setValue(`answers.${index}`, {
                answer,
                questionId: question.id,
                question: question.question
            });
        };

        return (
            <form ref={formRef} className={classes.root} onSubmit={handleSubmit(submitHandler)}>
                <div className={classes.grid}>
                    <Input
                        required
                        label="First Name"
                        error={!!errors.name}
                        {...register('name', { required: true })}
                    />
                    <Input
                        required
                        label="Last Name"
                        error={!!errors.lastName}
                        {...register('lastName', { required: true })}
                    />
                </div>
                <div className={classes.grid}>
                    <Input
                        required
                        type="email"
                        label="Email"
                        error={!!errors.email}
                        {...register('email', {
                            required: true,
                            pattern: {
                                value: /\S+@\S+\.\S+/,
                                message: 'Entered value does not match email format'
                            }
                        })}
                    />
                    <Controller
                        control={control}
                        name="phone"
                        rules={{
                            required: true,
                            validate: value => {
                                if (value?.replace(/\D/g, '').length !== 10) {
                                    return 'Phone number must be 10 digits';
                                }

                                return true;
                            }
                        }}
                        render={({ field }) => (
                            <PhoneInput
                                required
                                label="Phone Number"
                                error={!!errors.phone}
                                {...field}
                            />
                        )}
                    />
                </div>

                {isAddressRequired && (
                    <>
                        <input
                            type="hidden"
                            {...register('addresses.0.country')}
                            value={marketplaceAddress?.country}
                        />

                        <Input
                            required
                            label="Home Address"
                            error={!!errors.addresses?.[0]?.addressLineOne}
                            {...register('addresses.0.addressLineOne', { required: true })}
                        />

                        <div className={classes.threeColumnsGrid}>
                            <Input
                                required
                                label="City"
                                error={!!errors.addresses?.[0]?.city}
                                {...register('addresses.0.city', { required: true })}
                            />
                            <Input
                                required
                                label="State"
                                error={!!errors.addresses?.[0]?.state}
                                {...register('addresses.0.state', { required: true })}
                            />
                            <Controller
                                control={control}
                                name="addresses.0.zipCode"
                                rules={{
                                    required: true,
                                    validate: value => {
                                        if (value?.replace(/\D/g, '').length < 1) {
                                            return 'Zip code is required';
                                        }

                                        return true;
                                    }
                                }}
                                render={({ field }) => (
                                    <ReactInputMask mask="99999" {...field}>
                                        {() => (
                                            <Input
                                                {...field}
                                                required
                                                label="Zip Code"
                                                error={!!errors.addresses?.[0]?.zipCode}
                                            />
                                        )}
                                    </ReactInputMask>
                                )}
                            />
                        </div>
                    </>
                )}

                <div className={classes.header}>
                    <h2 className={clsx('h4 underlined', classes.title)}>Emergency Contact</h2>
                    <p>
                        Emergency contact can pick-up, drop off & make decisions on behalf of the
                        client&apos;s pet.
                    </p>

                    {!showEmergencyContact && (
                        <div>
                            <Button
                                size="medium"
                                variant="green"
                                onClick={() => setShowEmergencyContact(true)}
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                Add emergency contact
                            </Button>
                        </div>
                    )}
                </div>

                {showEmergencyContact && (
                    <>
                        <div className={classes.grid}>
                            <Input
                                required
                                label="First Name"
                                error={!!errors.emergencyContacts?.[0]?.name}
                                {...register('emergencyContacts.0.name', { required: true })}
                            />
                            <Input
                                required
                                label="Last Name"
                                error={!!errors.emergencyContacts?.[0]?.lastName}
                                {...register('emergencyContacts.0.lastName', { required: true })}
                            />
                        </div>
                        <div className={classes.grid}>
                            <Controller
                                control={control}
                                name="emergencyContacts.0.phone"
                                rules={{
                                    required: true,
                                    validate: value => {
                                        if (value?.replace(/\D/g, '').length !== 10) {
                                            return 'Phone number must be 10 digits';
                                        }

                                        return true;
                                    }
                                }}
                                render={({ field }) => (
                                    <PhoneInput
                                        required
                                        label="Phone Number"
                                        error={!!errors.emergencyContacts?.[0]?.phone}
                                        {...field}
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name="emergencyContacts.0.relationship"
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <SelectAutocomplete
                                        {...field}
                                        required
                                        label="Relationship"
                                        options={relationships}
                                        error={!!errors.emergencyContacts?.[0]?.relationship}
                                    />
                                )}
                            />
                        </div>

                        <button
                            type="button"
                            className={classes.deleteButton}
                            onClick={deleteEmergencyContactHandler}
                        >
                            <FontAwesomeIcon icon={faTrashAlt} />
                            <span>Delete</span>
                        </button>
                    </>
                )}

                {props.questions && (
                    <>
                        <div className={classes.header}>
                            <h2 className={clsx('h4 underlined mb-5', classes.title)}>
                                Questions for you
                            </h2>
                        </div>

                        <div className="flex flex-col gap-5">
                            {props.questions.map((item, index) => (
                                <Input
                                    multiline
                                    key={item.id}
                                    label={item.question}
                                    required={item.isRequired}
                                    placeholder="Write your answer"
                                    {...register(`answers.${index}.answer`, {
                                        required: true,
                                        onChange: e =>
                                            questionChangeHandler(index, item, e.target.value)
                                    })}
                                />
                            ))}
                        </div>
                    </>
                )}
            </form>
        );
    }
);

CustomerForm.displayName = 'CustomerForm';
